import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty, isNilOrEmptyOrZero, toBoolean } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { isEqual } from 'lodash-es';
import { baseSocialeCkHelper, getBaseSocialeControl } from './helpers/base-sociale-form.helper';

export const baseSocialeComposizioneSociRules: FormGroupRules = {
    my_NumMprima: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumMprima');
    },

    my_NumFprima: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumFprima');
    },

    my_NumMseconda: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumMseconda');
    },

    my_NumFseconda: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumFseconda');
    },

    my_NumMterza: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumMterza');
    },

    my_NumFterza: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumFterza');
    },

    my_NumSociMComunitari: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumSociMComunitari');
    },

    my_NumSociFComunitari: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumSociFComunitari');
    },

    my_NumSociMExtracomunitari: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumSociMExtracomunitari');
    },

    my_NumSociFExtracomunitari: (form: FormGroup) => {
        myNumRequiredHelper(form, 'my_NumSociFExtracomunitari');
    },

    my_CtrlSociSessoEta: (form: FormGroup) => {
        myCtrlSociHelper(form, 'my_CtrlSociSessoEta', 'my_TotSociPerSessoEta');
    },

    my_CtrlSociProvenienza: (form: FormGroup) => {
        myCtrlSociHelper(form, 'my_CtrlSociProvenienza', 'my_TotSociSessoProvenienza');
    },

    my_ckSoggLibrSociNoAttSociali: (form: FormGroup) => {
        const schedaCoopSocialiGroup = getGroup(form, formPaths.schedaCoopSociali);
        const my_ckSoggLibrSociNoAttSocialiControl = getBaseSocialeControl(form, 'my_gruppo17.my_ckSoggLibrSociNoAttSociali');
        const my_ckSoggLibrSociNoAttSociali: string = my_ckSoggLibrSociNoAttSocialiControl?.value;
        const my_CkNormeSocioLavorat: string = getBaseSocialeControl(form, 'my_CkNormeSocioLavorat')?.value;
        const my_UnitaSociUtil: string = getBaseSocialeControl(form, 'my_UnitaSociUtil')?.value;
        const my_SociFisCoopData: string = getBaseSocialeControl(form, 'my_SociFisCoopData')?.value;
        const my_SociSpecData: string = getBaseSocialeControl(form, 'my_SociSpecData')?.value;
        const my_ScSocObbl: string = getControl(schedaCoopSocialiGroup, 'my_ScSocObbl')?.value;
        const my_CkSociVolontari: string = getControl(schedaCoopSocialiGroup, 'my_CkSociVolontari')?.value;
        const my_SociVolontari: string = getControl(schedaCoopSocialiGroup, 'my_SociVolontari')?.value;
        const errors = my_ckSoggLibrSociNoAttSocialiControl.errors ? my_ckSoggLibrSociNoAttSocialiControl.errors : {};

        const showYesResError =
            toNum(my_ckSoggLibrSociNoAttSociali) !== 1 &&
            toNum(my_UnitaSociUtil) < toNum(my_SociFisCoopData) + toNum(my_SociSpecData) - toNum(my_ScSocObbl) * toNum(toBoolean(my_CkSociVolontari)) * toNum(my_SociVolontari) &&
            toNum(my_CkNormeSocioLavorat) === 1;

        addRemoveError(showYesResError, errors, 'my_ckSoggLibrSociNoAttSociali', {
            status: 'Il numero dei soci lavoratori è minore del numero dei soci persone fisiche e speciali.',
            message: "E' obbligatorio rispondere 'Si'",
        });

        addRemoveError(isNilOrEmpty(my_ckSoggLibrSociNoAttSociali) && my_CkNormeSocioLavorat === '1', errors, 'my_ckSoggLibrSociNoAttSociali', {
            status: REQUIRED_STATUS,
        });

        my_ckSoggLibrSociNoAttSocialiControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SociTechAmmini: (form: FormGroup) => {
        baseSocialeCkHelper(form, 'my_SociTechAmmini', 'my_CkNormeSocioLavorat', 1);
    },

    my_SociTechAmminiLav: (form: FormGroup) => {
        const my_SociTechAmminiLavControl = getBaseSocialeControl(form, 'my_SociTechAmminiLav');
        const my_SociTechAmmini = toNum(getBaseSocialeControl(form, 'my_SociTechAmmini')?.value);
        const errors = my_SociTechAmminiLavControl.errors ? my_SociTechAmminiLavControl.errors : {};
        const showError = toNum(my_SociTechAmminiLavControl?.value) > my_SociTechAmmini;

        addRemoveError(showError, errors, 'my_SociTechAmminiLav', {
            status: 'Totali non congruenti, verificare i soci tecnici ed amministrativi inseriti',
        });

        my_SociTechAmminiLavControl.setErrors(isEqual(errors, {}) ? null : errors);

        baseSocialeCkHelper(form, 'my_SociTechAmminiLav', 'my_CkNormeSocioLavorat', 1);
    },
};

const myNumRequiredHelper = (form: FormGroup, targetPath: string) => {
    const targetControl = getBaseSocialeControl(form, targetPath);
    const my_NumValue: string = targetControl?.value;
    const my_TotSociPersoneFisiche = toNum(getControl(form, 'my_TotSociPersoneFisiche')?.value);
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(isNilOrEmpty(my_NumValue) && !isNilOrEmptyOrZero(my_TotSociPersoneFisiche), errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

const myCtrlSociHelper = (form: FormGroup, targetPath: string, totPath: string) => {
    const targetControl = getBaseSocialeControl(form, targetPath);
    const my_TotSociPersoneFisiche = toNum(getControl(form, 'my_TotSociPersoneFisiche')?.value);
    const my_TotTarget = toNum(getControl(form, totPath)?.value);
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(my_TotSociPersoneFisiche !== my_TotTarget && my_TotSociPersoneFisiche !== 0, errors, targetPath, {
        status: 'Totali non corrispondenti',
        message: 'La somma dei 2 totali deve corrispondere ai soci fisici e ammessi a categorie speciali',
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
